import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Link,
} from '@mui/material';
import { doc, setDoc, getDoc, collection, getDocs, query, where, deleteField, updateDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';

interface Props {
  onConfigSaved: () => void;
}

export const KrakenConfig: React.FC<Props> = ({ onConfigSaved }) => {
  const { state } = useAuth();
  const [apiKey, setApiKey] = useState('');
  const [privateKey, setPrivateKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [migrated, setMigrated] = useState(false);

  // Load existing configuration if available
  useEffect(() => {
    const loadExistingConfig = async () => {
      if (!state.user) return;
      
      try {
        // Check for credentials in the new api_credentials subcollection
        const apiCredentialsRef = collection(db, `users/${state.user.id}/api_credentials`);
        const krakenQuery = query(apiCredentialsRef, where("provider", "==", "kraken"));
        const querySnapshot = await getDocs(krakenQuery);
        
        if (!querySnapshot.empty) {
          const credentialDoc = querySnapshot.docs[0];
          const credentialData = credentialDoc.data();
          
          if (credentialData) {
            setApiKey(credentialData.apiKey || '');
            setPrivateKey(credentialData.secretKey || credentialData.privateKey || '');
            console.log('Loaded Kraken API keys from api_credentials subcollection');
            return;
          }
        }
        
        // Fallback: Check for credentials in the old locations for backward compatibility
        let foundInLegacyLocation = false;
        let legacyApiKey = '';
        let legacyPrivateKey = '';
        
        // 1. Check user document preferences
        const userRef = doc(db, 'users', state.user.id);
        const userDoc = await getDoc(userRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const krakenConfig = userData.preferences?.apiKeys?.kraken;
          
          if (krakenConfig) {
            legacyApiKey = krakenConfig.apiKey || '';
            legacyPrivateKey = krakenConfig.privateKey || '';
            foundInLegacyLocation = true;
            console.log('Found Kraken API keys in user preferences (legacy location)');
          }
        }
        
        // 2. Check credentials subcollection
        if (!foundInLegacyLocation) {
          const credentialsRef = doc(db, 'users', state.user.id, 'credentials', 'kraken');
          const credentialsDoc = await getDoc(credentialsRef);
          
          if (credentialsDoc.exists()) {
            const credData = credentialsDoc.data();
            
            if (credData) {
              legacyApiKey = credData.apiKey || '';
              legacyPrivateKey = credData.secretKey || credData.privateKey || '';
              foundInLegacyLocation = true;
              console.log('Found Kraken API keys in credentials subcollection (legacy location)');
            }
          }
        }
        
        // 3. Check kraken_config collection
        if (!foundInLegacyLocation) {
          const krakenConfigRef = doc(db, 'kraken_config', state.user.id);
          const krakenConfigDoc = await getDoc(krakenConfigRef);
          
          if (krakenConfigDoc.exists()) {
            const configData = krakenConfigDoc.data();
            
            if (configData) {
              legacyApiKey = configData.apiKey || '';
              legacyPrivateKey = configData.secretKey || configData.privateKey || '';
              foundInLegacyLocation = true;
              console.log('Found Kraken API keys in kraken_config collection (legacy location)');
            }
          }
        }
        
        // If found in legacy location, migrate to new location
        if (foundInLegacyLocation && legacyApiKey && legacyPrivateKey) {
          setApiKey(legacyApiKey);
          setPrivateKey(legacyPrivateKey);
          setMigrated(true);
          
          // Automatically migrate to new location
          await setDoc(doc(apiCredentialsRef), {
            provider: "kraken",
            apiKey: legacyApiKey,
            secretKey: legacyPrivateKey,
            privateKey: legacyPrivateKey, // Keep for backward compatibility
            userId: state.user.id,
            email: state.user.email,
            updatedAt: new Date().toISOString(),
            migratedFrom: "legacy_storage"
          });
          
          console.log('Migrated Kraken API keys from legacy location to api_credentials subcollection');
          
          // Remove from user preferences if it exists there
          if (userDoc.exists()) {
            const userData = userDoc.data();
            if (userData.preferences?.apiKeys?.kraken) {
              try {
                await updateDoc(userRef, {
                  "preferences.apiKeys.kraken": deleteField()
                });
                console.log('Removed Kraken API keys from user preferences after migration');
              } catch (err) {
                console.error('Error removing Kraken API keys from user preferences:', err);
              }
            }
          }
          
          // Remove from credentials subcollection if it exists there
          try {
            const credentialsRef = doc(db, 'users', state.user.id, 'credentials', 'kraken');
            const credentialsDoc = await getDoc(credentialsRef);
            if (credentialsDoc.exists()) {
              await setDoc(credentialsRef, { 
                apiKey: deleteField(),
                privateKey: deleteField(),
                secretKey: deleteField(),
                migratedTo: "api_credentials",
                migratedAt: new Date().toISOString()
              }, { merge: true });
              console.log('Removed Kraken API keys from credentials subcollection after migration');
            }
          } catch (err) {
            console.error('Error removing Kraken API keys from credentials subcollection:', err);
          }
          
          // Remove from kraken_config collection if it exists there
          try {
            const krakenConfigRef = doc(db, 'kraken_config', state.user.id);
            const krakenConfigDoc = await getDoc(krakenConfigRef);
            if (krakenConfigDoc.exists()) {
              await setDoc(krakenConfigRef, { 
                apiKey: deleteField(),
                privateKey: deleteField(),
                secretKey: deleteField(),
                migratedTo: "api_credentials",
                migratedAt: new Date().toISOString()
              }, { merge: true });
              console.log('Removed Kraken API keys from kraken_config collection after migration');
            }
          } catch (err) {
            console.error('Error removing Kraken API keys from kraken_config collection:', err);
          }
        }
      } catch (err) {
        console.error('Error loading Kraken config:', err);
      }
    };
    
    loadExistingConfig();
  }, [state.user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    if (!apiKey || !privateKey) {
      setError('API Key and Private Key are required');
      setLoading(false);
      return;
    }

    try {
      if (!state.user) {
        throw new Error('User not authenticated');
      }
      
      // Save to the new api_credentials subcollection
      const apiCredentialsRef = collection(db, `users/${state.user.id}/api_credentials`);
      const krakenQuery = query(apiCredentialsRef, where("provider", "==", "kraken"));
      const querySnapshot = await getDocs(krakenQuery);
      
      // Create a new document in the subcollection or update existing one
      if (querySnapshot.empty) {
        // Create new document in subcollection
        await setDoc(doc(apiCredentialsRef), {
          provider: "kraken",
          apiKey,
          secretKey: privateKey,
          privateKey: privateKey, // Keep for backward compatibility
          userId: state.user.id,
          email: state.user.email,
          updatedAt: new Date().toISOString()
        });
        console.log('Created new Kraken API keys in api_credentials subcollection');
      } else {
        // Update existing document
        const credentialDoc = querySnapshot.docs[0];
        await setDoc(credentialDoc.ref, {
          provider: "kraken",
          apiKey,
          secretKey: privateKey,
          privateKey: privateKey, // Keep for backward compatibility
          userId: state.user.id,
          email: state.user.email,
          updatedAt: new Date().toISOString()
        });
        console.log('Updated existing Kraken API keys in api_credentials subcollection');
      }

      setSuccess(true);
      setMigrated(false);
      onConfigSaved();
    } catch (err) {
      setError('Failed to save Kraken API keys. Please try again.');
      console.error('Kraken API key save error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Kraken Configuration
        </Typography>
        
        <Typography variant="body2" color="textSecondary" paragraph>
          Enter your Kraken API keys below. These keys will be stored securely in your user profile and used to access your Kraken account data.{' '}
          <Link href="https://www.kraken.com/u/security/api" target="_blank" rel="noopener">
            Create API Key
          </Link>
        </Typography>

        {migrated && (
          <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
            Your Kraken API keys have been migrated from a legacy storage location to the new secure storage system.
          </Alert>
        )}

        {apiKey && privateKey && !migrated && (
          <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
            You have existing Kraken API keys configured. You can update them below if needed.
          </Alert>
        )}

        {!state.user && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography>
              You are not logged in. Please log in to save your Kraken API keys.
            </Typography>
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
          <TextField
            fullWidth
            label="API Key"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            margin="normal"
            required
          />
          
          <TextField
            fullWidth
            label="Private Key"
            value={privateKey}
            onChange={(e) => setPrivateKey(e.target.value)}
            margin="normal"
            required
            multiline
            rows={3}
          />

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mt: 2 }}>
              Kraken API keys saved successfully!
            </Alert>
          )}

          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading || !state.user}
            sx={{ mt: 2 }}
          >
            {loading ? <CircularProgress size={24} /> : 'Save API Keys'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};
